import {
  OptimizationHistoryItem,
  OptimizationHistoryQuery
} from '@core/models/optimization-history.model';
import { JobStatusV4, OptimizationResult } from '@core/models/optimization.model';
import * as PlanActions from '@core/store/plan/plan.actions';

export const OPTIMIZATION_HISTORY_FEATURE_KEY = 'optimizationHistory';

export const OPTIMIZATION_HISTORY_PAGE_SIZE = 25;

export type OptimizationEvent =
  | ReturnType<typeof PlanActions.optimizationUpdated>
  | ReturnType<typeof PlanActions.optimizationDeleted>;

export interface OptimizationHistoryState {
  items: OptimizationHistoryItem[];
  total: number;
  query: OptimizationHistoryQuery;
  loading: boolean;
  /**
   * Events received since the page was requested. The server may have read the page before any of
   * them, so they are replayed over the response.
   */
  eventsDuringLoad: OptimizationEvent[];
  hasUnseenJobs: boolean;
  pageOpen: boolean;
}

export const initialOptimizationHistoryQuery: OptimizationHistoryQuery = {
  page: 1,
  take: OPTIMIZATION_HISTORY_PAGE_SIZE,
  statuses: [],
  includeDeletedPlans: false
};

export const initialOptimizationHistoryState: OptimizationHistoryState = {
  items: [],
  total: 0,
  query: initialOptimizationHistoryQuery,
  loading: false,
  eventsDuringLoad: [],
  hasUnseenJobs: false,
  pageOpen: false
};

/**
 * Whether an event announces a job the loaded page does not hold but the active query would list.
 * The business unit and launcher are not in the payload, so those filters are assumed to match.
 */
export function isUnseenMatchingCreation(
  state: OptimizationHistoryState,
  result: OptimizationResult
): boolean {
  const { query } = state;
  return (
    !!result.jobId &&
    result.status === JobStatusV4.CREATED &&
    !state.items.some((item) => item.id === result.jobId) &&
    (query.statuses.length === 0 || query.statuses.includes(JobStatusV4.CREATED)) &&
    (query.dataSpaceId === undefined || query.dataSpaceId === result.dataSpaceId)
  );
}

/** Whether an event just gave a loaded row an output plan, which events carry without its name. */
export function isNewOutputPlanOnPage(
  state: OptimizationHistoryState,
  result: OptimizationResult
): boolean {
  return (
    !!result.outputPlanId &&
    state.items.some(
      (item) =>
        item.id === result.jobId &&
        item.outputPlan?.id === result.outputPlanId &&
        item.outputPlan?.name === null
    )
  );
}
